/**
 * Settings cache utility to prevent multiple API calls
 */

import { service } from '@/services/singleton.service';

const SETTINGS_CACHE_KEY = 'appSettings';
const CACHE_TIMESTAMP_KEY = 'appSettingsTimestamp';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

let settingsPromise = null; // Singleton promise to prevent concurrent calls

/**
 * Get cached settings from localStorage
 */
function getCachedSettings() {
  try {
    const cached = localStorage.getItem(SETTINGS_CACHE_KEY);
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    
    if (cached && timestamp) {
      const age = Date.now() - parseInt(timestamp, 10);
      if (age < CACHE_DURATION) {
        return JSON.parse(cached);
      }
    }
  } catch (error) {
    console.error('Error reading cached settings:', error);
  }
  return null;
}

/**
 * Cache settings to localStorage
 */
function setCachedSettings(settings) {
  try {
    localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(settings));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('Error caching settings:', error);
  }
}

/**
 * Clear cached settings
 */
export function clearSettingsCache() {
  localStorage.removeItem(SETTINGS_CACHE_KEY);
  localStorage.removeItem(CACHE_TIMESTAMP_KEY);
}

/**
 * Fetch settings from API (with caching and singleton pattern)
 * @param {boolean} forceRefresh - Force refresh from API, ignore cache
 * @returns {Promise} Settings data
 */
export async function getSettings(forceRefresh = false) {
  // Check cache first if not forcing refresh
  if (!forceRefresh) {
    const cached = getCachedSettings();
    if (cached) {
      return { data: cached };
    }
  }

  // If there's already a pending request, return that promise
  if (settingsPromise) {
    return settingsPromise;
  }

  // Create new request
  settingsPromise = service.settings.get()
    .then(res => {
      if (res?.data) {
        setCachedSettings(res.data);
      }
      settingsPromise = null; // Clear promise after completion
      return res;
    })
    .catch(error => {
      settingsPromise = null; // Clear promise on error
      throw error;
    });

  return settingsPromise;
}

